import { useEffect, type ReactNode } from 'react';
import { SiteFrame } from '../components/SiteFrame.jsx';
import { useViewer } from '../lib/viewer.js';

/**
 * What tomo is for, at `/about`.
 *
 * Laid out like the rest of the frame's wayfinding: numbered stops, one thick
 * brand rule over each, and a list of what you will find there. It describes
 * what is LIVE. Rewards and collectibles are behind a flag and stay off this
 * page until they are not, for the same reason the privacy page lists only
 * what is kept: a page that promises more than the product is a page nobody
 * can trust about the rest.
 *
 * Public, like the legal pages. It is what a link from a Discord lands on when
 * somebody asks "what is this".
 */

/** Whether this path is the about page. A trailing slash is the same page. */
export function isAboutPage(pathname: string): boolean {
  return pathname.replace(/\/+$/, '') === '/about';
}

export function About(): ReactNode {
  const viewer = useViewer();
  useEffect(() => {
    document.title = 'About · tomo';
  }, []);

  return (
    <SiteFrame viewer={viewer} trail={[{ label: 'About' }]}>
      <div className="flex max-w-2xl flex-col gap-12">
        <header className="flex flex-col gap-4">
          <h1 className="text-3xl font-extrabold tracking-[-0.03em] text-ink-50">
            Watch with your friends.
          </h1>
          <p className="text-lg leading-relaxed text-ink-200">
            tomo puts several Twitch streams in one room and reads all of their chats as one. It&rsquo;s
            for the nights when the people you watch are playing together, or the same game, or you
            just want everyone on one screen, and a room of friends to watch it with.
          </p>
          <p className="text-[15px] leading-relaxed text-ink-400">
            Tomo (友) means friend. Reading a room needs no account. Sign in with Twitch to chat, see
            who you follow, and keep your rooms.
          </p>
          <div className="flex flex-wrap gap-3 pt-1">
            <a
              href="/live/"
              className="rounded-md bg-brand-400 px-4 py-2 text-sm font-bold text-ink-950 transition hover:bg-brand-300"
            >
              Open a room
            </a>
            <a
              href="/"
              className="rounded-md border border-ink-700 px-4 py-2 text-sm font-semibold text-ink-200 transition hover:border-brand-300 hover:text-brand-300"
            >
              See who&rsquo;s live
            </a>
          </div>
        </header>

        <Stop number="01" title="Communities">
          <p>
            A community is a home for a group of streamers: a game&rsquo;s scene, a friend group, a
            team. It lives at its own address, <Code>tomo.channel/its-name</Code>, and anyone can start
            one.
          </p>
          <Features>
            <Feature name="Who’s live, at a glance">
              The community page shows who is streaming right now with a preview, and everyone
              playing the community&rsquo;s own game comes first. Cards on the front page say how
              many are live and how many people are watching on tomo.
            </Feature>
            <Feature name="A roster that scales">
              Up to 500 channels, sorted into the community&rsquo;s own groupings. Paste a whole list
              at once, or follow a roster someone else keeps and let it update itself.
            </Feature>
            <Feature name="Its own room">
              One click opens the community&rsquo;s room with whoever is live: two on screen, the
              rest one tap away.
            </Feature>
            <Feature name="Room chat">
              Everyone in the room can talk to each other in tomo&rsquo;s own chat, alongside the
              streamers&rsquo; Twitch chats.
            </Feature>
            <Feature name="Go-live alerts">
              When someone on the roster goes live, the room says so and offers to add them. The
              community can post it to its Discord too, with the streamer&rsquo;s face and name, and
              let the people it picks send announcements.
            </Feature>
            <Feature name="Run by its people">
              An owner appoints editors, who write the description, pick the game, manage the roster
              and groupings, and keep room chat decent with mutes and kicks.
            </Feature>
          </Features>
        </Stop>

        <Stop number="02" title="The live room">
          <p>
            A room is the screen you watch in. Make one from your follows in two clicks: watch now
            from a link, or keep it as a room, public or unlisted.
          </p>
          <Features>
            <Feature name="Every stream, fitted">
              The grid fits your window rather than a fixed layout, so two streams stack in a tall
              window and sit side by side in a wide one. On a phone, one stream takes the stage and
              the rest wait in a strip.
            </Feature>
            <Feature name="Every chat, as one">
              All the chats in the room read as a single timeline, each line marked with its
              channel&rsquo;s colour and face. Tap a face to read just that chat. Lines that mention
              you are highlighted, and the chat marks where one day ends and the next begins.
            </Feature>
            <Feature name="Controls per channel">
              For each channel, choose video (off, mini or full), sound (off, on, or focus), captions,
              chat and events. Focus puts one stream in front and turns the others down instead of
              muting them.
            </Feature>
            <Feature name="Captions">
              Live captions appear inline in the chat, stitched back into whole sentences.
            </Feature>
            <Feature name="What’s happening">
              Subs, gift subs, raids and announcements appear as they happen, plus hype trains, polls
              and predictions on channels connected to tomo.
            </Feature>
            <Feature name="One box to talk anywhere">
              Send to any channel&rsquo;s Twitch chat as yourself, or to the room, from the same box.
              Emotes and names complete as you type, your saved emotes work in every chat, and
              Up and Down bring back what you sent.
            </Feature>
            <Feature name="Search what you’ve seen">
              Search every chat in the room, and find a person under every name they have used.
            </Feature>
            <Feature name="Adding channels">
              Pick from your follows, with who is live, or search all of Twitch. Add a channel with
              its stream, or just its chat.
            </Feature>
            <Feature name="Who’s here">
              See who else is in the room. Sharing what you&rsquo;re watching is off until you turn
              it on.
            </Feature>
            <Feature name="Blocking">
              Block someone and their room chat and presence are hidden from you everywhere on tomo.
            </Feature>
            <Feature name="Moderator tools">
              If you moderate a channel on Twitch, moderate it from the room: timeouts, bans and
              slash commands with completion, AutoMod&rsquo;s held messages to allow or deny, and one
              feed for every channel you moderate. Every action goes to Twitch as you, and nothing
              fires from a single click.
            </Feature>
            <Feature name="Keyboard shortcuts">
              Move between chats, search and add channels without the mouse. Press{' '}
              <Code>?</Code> in a room for the list.
            </Feature>
          </Features>
        </Stop>

        <Stop number="03" title="The small print, in large print">
          <p>
            tomo is free. There are no ads and no trackers, and we don&rsquo;t sell your data. Twitch
            chat comes straight from Twitch to your browser. The{' '}
            <a href="/privacy" className="text-brand-300 underline-offset-2 hover:underline">privacy page</a>{' '}
            lists everything we keep. tomo isn&rsquo;t made by, or affiliated with, Twitch or Discord.
          </p>
          <p>
            Streamers can connect their own channel from the{' '}
            <a href="/studio" className="text-brand-300 underline-offset-2 hover:underline">studio</a>.
            Found something broken? Report it from the menu behind your face.
          </p>
        </Stop>
      </div>
    </SiteFrame>
  );
}

/** One numbered stop: a thick rule, the number, the name. */
function Stop({ number, title, children }: {
  readonly number: string;
  readonly title: string;
  readonly children: ReactNode;
}): ReactNode {
  return (
    <section className="flex flex-col gap-5 border-t-[3px] border-brand-400 pt-5">
      <h2 className="flex items-baseline gap-4">
        <span className="text-4xl leading-none font-extrabold tracking-[-0.04em] text-brand-400 tabular-nums">
          {number}
        </span>
        <span className="text-xl font-bold tracking-tight text-ink-50">{title}</span>
      </h2>
      <div className="flex flex-col gap-5 text-[15px] leading-relaxed text-ink-200">{children}</div>
    </section>
  );
}

function Features({ children }: { readonly children: ReactNode }): ReactNode {
  return <dl className="grid gap-x-8 gap-y-5 sm:grid-cols-2">{children}</dl>;
}

function Feature({ name, children }: { readonly name: string; readonly children: ReactNode }): ReactNode {
  return (
    <div className="flex flex-col gap-1 border-l-2 border-ink-800 pl-3">
      <dt className="font-semibold text-ink-50">{name}</dt>
      <dd className="text-[14px] leading-relaxed text-ink-400">{children}</dd>
    </div>
  );
}

function Code({ children }: { readonly children: ReactNode }): ReactNode {
  return (
    <code className="rounded bg-ink-900 px-1.5 py-0.5 font-mono text-[13px] text-ink-50">{children}</code>
  );
}
